import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, KeyRound, Lock, Unlock, ShieldCheck, Trash2, LogOut } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, Modal, useToast, useConfirm, Avatar } from "@/components/ui";
import { Field, TextInput } from "@/components/fields";
import { fmtDateTime } from "@/lib/format";

export function AdminUsers() {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, node: confirmNode } = useConfirm();
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: () => api.get<{ users: any[] }>(`/users?q=${encodeURIComponent(q)}`),
  });
  const { data: rolesData } = useQuery({ queryKey: ["roles"], queryFn: () => api.get<{ roles: any[] }>("/roles") });
  const roles = rolesData?.roles ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: any) => api.put(`/users/${id}`, { isActive }),
    onSuccess: () => { invalidate(); toast.push("success", "Updated"); },
  });
  const resetPw = useMutation({
    mutationFn: (id: string) => api.post<{ tempPassword: string }>(`/users/${id}/reset-password`, {}),
    onSuccess: (d) => { navigator.clipboard?.writeText(d.tempPassword).catch(() => {}); toast.push("success", `Temp password: ${d.tempPassword} (copied)`); },
  });
  const unlock = useMutation({ mutationFn: (id: string) => api.post(`/users/${id}/unlock`, {}), onSuccess: () => { invalidate(); toast.push("success", "Account unlocked"); } });
  const revokeSessions = useMutation({
    mutationFn: (id: string) => api.post<{ revoked: number }>(`/users/${id}/revoke-sessions`, {}),
    onSuccess: (d) => toast.push("success", d.revoked ? `Signed out ${d.revoked} active session(s)` : "No active sessions to sign out"),
  });
  const removeUser = useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: () => { invalidate(); toast.push("success", "User deleted"); },
    onError: (e) => toast.push("error", e instanceof ApiError ? e.message : "Could not delete user"),
  });

  return (
    <>
      <PageHeader title="Users" subtitle="Create accounts, assign roles, manage access."
        actions={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New user</button>} />

      <div className="relative mb-3 max-w-xs">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-ink-faint" />
        <input className="input !pl-8" placeholder="Search name, username, email…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {isLoading ? <PageLoader /> : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full min-w-[860px] text-sm">
            <thead><tr>{["User", "Roles", "Status", "2FA", "Last login", ""].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
            <tbody>
              {usersData?.users.map((u) => (
                <tr key={u.id} className="border-b border-line hover:bg-surface-2/40">
                  <td className="td">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={u.fullName} color={u.avatarColor} size={30} />
                      <div>
                        <p className="font-medium">{u.fullName} {u.isSuperAdmin && <ShieldCheck size={12} className="ml-1 inline text-accent" />}</p>
                        <p className="text-xs text-ink-faint">@{u.username} · {u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r: any) => <span key={r.id} className="chip bg-surface-2 text-ink-soft text-[11px]">{r.name}</span>)}
                      {!u.roles.length && <span className="text-xs text-ink-faint">—</span>}
                    </div>
                  </td>
                  <td className="td">
                    <span className={`chip text-[11px] ${u.isActive ? "bg-st-ontrack/15 text-st-ontrack" : "bg-st-delayed/15 text-st-delayed"}`}>
                      {u.isActive ? "Active" : "Disabled"}
                    </span>
                    {u.mustChangePassword && <span className="ml-1 chip bg-accent/15 text-accent text-[11px]">Temp pw</span>}
                  </td>
                  <td className="td">{u.totpEnabled ? "✓" : "—"}</td>
                  <td className="td text-xs text-ink-faint">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : "never"}</td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <button className="btn-ghost !p-1.5" title="Edit roles" onClick={() => setEditing(u)}><ShieldCheck size={14} /></button>
                      <button className="btn-ghost !p-1.5" title="Reset password" onClick={() => resetPw.mutate(u.id)}><KeyRound size={14} /></button>
                      <button className="btn-ghost !p-1.5" title="Unlock" onClick={() => unlock.mutate(u.id)}><Unlock size={14} /></button>
                      <button className="btn-ghost !p-1.5" title="Sign out everywhere"
                        onClick={async () => { if (await confirm("Sign out everywhere", `End every active session for ${u.fullName}? They will need to sign in again.`)) revokeSessions.mutate(u.id); }}>
                        <LogOut size={14} />
                      </button>
                      {u.id !== me?.id && (
                        <button className="btn-ghost !p-1.5" title={u.isActive ? "Disable" : "Enable"}
                          onClick={() => setActive.mutate({ id: u.id, isActive: !u.isActive })}>
                          <Lock size={14} className={u.isActive ? "" : "text-st-delayed"} />
                        </button>
                      )}
                      {me?.isSuperAdmin && u.id !== me?.id && (
                        <button className="btn-ghost !p-1.5 text-ink-faint hover:text-st-delayed" title="Delete user"
                          onClick={async () => {
                            if (await confirm("Delete user", `Permanently delete ${u.fullName} (@${u.username})? This cannot be undone. Their past activity stays in the audit log; anything they owned or were assigned becomes unassigned.`, true)) {
                              removeUser.mutate(u.id);
                            }
                          }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <UserModal roles={roles} onClose={() => { setCreating(false); invalidate(); }} />}
      {editing && <UserModal roles={roles} user={editing} onClose={() => { setEditing(null); invalidate(); }} />}
      {confirmNode}
    </>
  );
}

function UserModal({ roles, user, onClose }: { roles: any[]; user?: any; onClose: () => void }) {
  const toast = useToast();
  const { user: me } = useAuth();
  const isEdit = !!user;
  const canRenameUsername = isEdit && !!me?.isSuperAdmin;
  const [f, setF] = useState({
    fullName: user?.fullName ?? "", username: user?.username ?? "", email: user?.email ?? "",
    jobTitle: user?.jobTitle ?? "", password: "",
    roleIds: user?.roles?.map((r: any) => r.id) ?? [] as string[],
  });
  const [err, setErr] = useState("");
  const [tempPw, setTempPw] = useState("");

  const save = useMutation({
    mutationFn: () => isEdit
      ? api.put(`/users/${user.id}`, {
          fullName: f.fullName, email: f.email, jobTitle: f.jobTitle, roleIds: f.roleIds,
          ...(canRenameUsername && f.username !== user.username ? { username: f.username } : {}),
        })
      : api.post<{ tempPassword?: string }>("/users", { ...f, returnTempPassword: !f.password }),
    onSuccess: (d: any) => {
      if (!isEdit && d?.tempPassword) { setTempPw(d.tempPassword); toast.push("success", "User created"); return; }
      toast.push("success", isEdit ? "User updated" : "User created");
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  const toggleRole = (id: string) => setF((s) => ({ ...s, roleIds: s.roleIds.includes(id) ? s.roleIds.filter((x: string) => x !== id) : [...s.roleIds, id] }));

  if (tempPw) {
    return (
      <Modal open onClose={onClose} title="User created"
        footer={<button className="btn-primary" onClick={onClose}>Done</button>}>
        <p className="text-sm text-ink-soft">Share this temporary password securely. The user must change it at first sign-in.</p>
        <code className="mt-3 block rounded-lg bg-surface-2 p-3 text-center text-lg font-bold tracking-wider">{tempPw}</code>
        <button className="btn-outline mt-2 w-full text-xs" onClick={() => navigator.clipboard?.writeText(tempPw)}>Copy to clipboard</button>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit ${user.fullName}` : "New user"} wide
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={save.isPending} onClick={() => { setErr(""); save.mutate(); }}>{isEdit ? "Save" : "Create user"}</button>
      </>}>
      {err && <div className="mb-3 rounded-lg bg-st-delayed/10 px-3 py-2 text-sm text-st-delayed">{err}</div>}
      <div className="grid gap-3.5 sm:grid-cols-2">
        <Field label="Full name" required><TextInput value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></Field>
        <Field label="Job title"><TextInput value={f.jobTitle} onChange={(e) => setF({ ...f, jobTitle: e.target.value })} /></Field>
        <Field label="Username" required hint={isEdit && !canRenameUsername ? "Only a Super Admin can rename a username" : undefined}>
          <TextInput value={f.username} disabled={isEdit && !canRenameUsername}
            onChange={(e) => setF({ ...f, username: e.target.value.trim().toLowerCase() })} />
        </Field>
        <Field label="Email" required><TextInput type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        {!isEdit && (
          <div className="sm:col-span-2">
            <Field label="Password" hint="Leave blank to auto-generate a temporary password">
              <TextInput type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Auto-generated if blank" />
            </Field>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="label">Roles</p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {roles.map((r) => (
            <label key={r.id} className="flex items-start gap-2 rounded-lg border border-line p-2.5 text-sm">
              <input type="checkbox" className="mt-0.5" checked={f.roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
              <span><span className="font-medium">{r.name}</span><span className="block text-xs text-ink-faint">{r.description}</span></span>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}
